import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { whenOptions } from "../../config/constants/options";
import { AppDispatch } from "../store";
import {
  getConsultationDocuments,
  getDocuments,
} from "../../services/media.services";
import { Complaint, ConsultationState, Diagnosis, Prescription } from "../../types/consultation";

const initialState: ConsultationState = {
  formData: {
    weight: "",
    height: "",
    pulse: "",
    temperature: "",
    bp: "",
    complaint: "",
    frequency: "",
    severity: "",
    duration: "",
    date: null,
    clinicalHistory: "",
    additionalNotes: "",
    advice: "",
    test: [],
    unit: "",
    consultFlag: null,
    uuid: "",
    customerId: "",
    markDone: false,
    testInputValue: "",
  },
  errors: {},
  selectOptions: {
    complaint: [],
    frequency: [],
    severity: [],
    medicine: [],
    dose: [],
    when: whenOptions,
    test: [],
    diagnosis: [],
  },
  tables: {
    diagnosis: [{ no: 0, diagnosis: "", duration: "", date: null, unit: "" }],
    prescription: [
      {
        no: 0,
        medicine: "",
        dose: "",
        when: "",
        frequency: "",
        unit_quantity: "",
        notes: "",
        originalNotes: "",
        language: "en",
        unit: "",
      },
    ],
    complaints: [
      {
        complaint: "",
        frequency: "",
        severity: "",
        duration: "",
        unit: "",
        date: null,
      } as Complaint,
    ],
  },
  pastConsultationData: {},
  mainPaymentHistory: [],
  mediaData: {},
  mediaTabData: {},
  hasUnsavedChanges: false,
};

const consultationSlice = createSlice({
  name: "consultation",
  initialState,
  reducers: {
    setFormData: (
      state,
      action: PayloadAction<Partial<ConsultationState["formData"]> & { skipUnsavedFlag?: boolean }>
    ) => {
      const { skipUnsavedFlag, ...rest } = action.payload as any;
      state.formData = { ...state.formData, ...rest };
      // Set hasUnsavedChanges to true when form data is modified (unless skipUnsavedFlag is true)
      if (!skipUnsavedFlag) {
        state.hasUnsavedChanges = true;
      }
    },
    setSelectOptions: (
      state,
      action: PayloadAction<Partial<ConsultationState["selectOptions"]>>
    ) => {
      state.selectOptions = { ...state.selectOptions, ...action.payload };
    },
    setErrors: (state, action: PayloadAction<Record<string, string>>) => {
      state.errors = action.payload;
    },
    setPastConsultationData: (state, action: PayloadAction<any>) => {
      state.pastConsultationData = action.payload;
    },
    setMediaData: (state, action: PayloadAction<any>) => {
      state.mediaData = action.payload;
    },
    setMediaTabData: (state, action: PayloadAction<any>) => {
      state.mediaTabData = action.payload;
    },
    setPaymentHistoryData: (state, action: PayloadAction<any>) => {
      state.mainPaymentHistory = action.payload;
    },
    setTable: (
      state,
      action: PayloadAction<{
        table: "diagnosis" | "prescription" | "complaints";
        rows: Diagnosis[] | Prescription[] | Complaint[];
        skipUnsavedFlag?: boolean;
      }>
    ) => {
      const { table, rows } = action.payload;
      if (table === "diagnosis") {
        state.tables.diagnosis = rows as Diagnosis[];
      } else if (table === "prescription") {
        state.tables.prescription = rows as Prescription[];
      } else if (table === "complaints") {
        state.tables.complaints = rows as Complaint[];
      }
      // Set hasUnsavedChanges to true when table data is modified (unless skipUnsavedFlag is true)
      if (!action.payload.skipUnsavedFlag) {
        state.hasUnsavedChanges = true;
      }
    },
    clearErrors: (state) => {
      state.errors = {};
    },
    clearAPIData: (state) => {
      state.pastConsultationData = {};
      state.mainPaymentHistory = [];
      state.mediaData = {};
    },
    clearForm: (state) => {
      console.log('Checking state.formData.uuid:', state.formData.uuid);
      state.formData = {
        ...initialState.formData,
        customerId: state.formData.customerId,
        uuid: state.formData.uuid,
      };
      state.errors = {};
      state.pastConsultationData = {};
      state.mainPaymentHistory = [];
      state.mediaData = {};
      state.tables = {
        diagnosis: [
          { no: 0, diagnosis: "", duration: "", date: null, unit: "" },
        ],
        prescription: [
          {
            no: 0,
            medicine: "",
            dose: "",
            when: "",
            frequency: "",
            unit_quantity: "",
            notes: "",
            originalNotes: "",
            language: "en",
            unit: "",
          },
        ],
        complaints: [
          {
            complaint: "",
            frequency: "",
            severity: "",
            duration: "",
            unit: "",
            date: null,
          } as Complaint,
        ],
      };
      state.hasUnsavedChanges = false;
    },
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },
  },
});

export const {
  setFormData,
  setErrors,
  clearErrors,
  clearForm,
  setSelectOptions,
  setTable,
  setPastConsultationData,
  setPaymentHistoryData,
  setMediaData,
  setMediaTabData,
  clearAPIData,
  setHasUnsavedChanges,
} = consultationSlice.actions;

// Thunk to handle fetching media data by ID
export const getMediaDataById =
  (caseId: string): any =>
  async (dispatch: AppDispatch) => {
    try {
      const res = await getConsultationDocuments(caseId);
      if (res?.data?.status === 200) {
        dispatch(setMediaData(res?.data?.data));
      }
    } catch (err) {
      dispatch(setMediaData({}));
    }
  };

export const getMediaTabDataById =
  (caseId: string): any =>
  async (dispatch: AppDispatch) => {
    try {
      const res = await getDocuments(caseId);
      if (res?.data?.status === 200) {
        dispatch(setMediaTabData(res?.data?.data));
      }
    } catch (err) {
      dispatch(setMediaTabData({}));
    }
  };

export default consultationSlice.reducer;
